<template>
  <div>
    <transition name="drawer-slide" appear>
      <div v-if="isOpen" class="fixed inset-0 z-50">
        <div class="absolute inset-0 backdrop" @click="close" />
        <div
          ref="cartDrawer"
          class="cart-drawer md:max-w-[500px] lg:w-1/2 absolute top-0 bottom-0 right-0 flex flex-col h-full bg-white"
        >
          <div
            class="flex items-center justify-between shrink-0 w-full border-b h-header-bar-height-mobile lg:h-header-bar-height"
          >
            <div class="px-4 lg:pl-6">
              <h2 class="font-medium leading-relaxed fluid-base">{{ $t('cart.title') }} {{ totalQuantity }}</h2>
            </div>
            <button
              type="button"
              ref="closeButton"
              class="flex items-center justify-center h-full px-4 transition-all text-blackly lg:px-6"
              @click.prevent="close"
            >
              <span class="sr-only">{{ $t('cart.closeDrawer') }}</span>
              <IconClose class="w-4 h-4" />
            </button>
          </div>
          <Cart />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { mixin as onClickOutside } from 'vue-on-click-outside';
import Cart from '@/components/cart/Cart';
import IconClose from '@/components/layout/header/IconClose';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useCartStore } from '@/stores/cart';
import { storeToRefs } from 'pinia';

// Store setup
const cartStore = useCartStore();
const { isOpen, cartCount } = storeToRefs(cartStore);
const totalQuantity = computed(() => (cartCount.value > 0 ? `(${cartCount.value})` : null));

// Actions
const close = () => cartStore.close();

// Focus trap setup
const cartDrawer = ref(null);
const { activate, deactivate } = useFocusTrap(cartDrawer);

const handleKeyDown = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
};

// Watchers and lifecycle hooks
watch(isOpen, (newValue) => {
  document.body.classList.toggle('overflow-hidden', newValue);

  if (newValue) {
    nextTick(() => {
      activate();
    });
  } else {
    deactivate();
  }
});

onMounted(async () => {
  await cartStore.initCart();

  if (isOpen.value) {
    nextTick(() => {
      activate();
    });
  }

  window.addEventListener('keydown', handleKeyDown);

  // FIXME: debug cart functions
  // await cartStore.refreshCheckout();
  // await cartStore.applyDiscount('HOLGER_ENROLLMENT');
  // await cartStore.createOrUpdateCartAttributes([{ key: 'test', value: 'test' }]);
});

onBeforeUnmount(() => {
  deactivate();
});

// Apply onClickOutside mixin functionality
defineExpose({
  mixins: [onClickOutside],
});
</script>

<style scoped>
@reference "~/assets/css/main.css";
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: all 300ms linear;
}

.cart-drawer {
  transition: transform 300ms ease;
  width: calc(100% - 3.5em);
}

.drawer-slide-enter .backdrop,
.drawer-slide-leave-to .backdrop {
  @apply opacity-0;
}

.drawer-slide-enter .cart-drawer,
.drawer-slide-leave-to .cart-drawer {
  @apply translate-x-full;
}
.backdrop {
  @apply transition-opacity delay-300 z-[-1];
  background-color: rgba(0, 0, 0, 0.75);
}
</style>
